import { t } from 'i18next';
import { getFileSizeInMb } from '../utils/getFileSize';

export interface ValidRespType {
  isValid: boolean;
  message: string;
}

type PatternType = {
  regExp: RegExp | string;
  message: string;
};

interface SimpleValidationProps {
  required?: boolean;
  nullable?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: PatternType;
}

interface FileValidationProps {
  maxSize?: number,
  acceptedFormats?: string[]
}

const getSymbolsWordByLength = (length: number) => {
  if (length >= 2 && length < 5) {
    return 'validation.symbols.many';
  } else if (length >= 5) {
    return 'validation.symbols.others';
  } else {
    return 'validation.symbols.one';
  }
};

export const validateIf = (statement: boolean, validator: (x: any) => string | undefined) => {
  if (statement) {
    return validator;
  }

  return () => {};
}

export const required = (value: string) => {
  if (value === null || value === undefined || value.length === 0) {
    return t('validation.fieldRequiredEmpty');
  }
};

export const minLength = (min: number) => {
  return (value: string | null = '') => {
    if (value !== null && value.length > 0 && value.length < min) {
      return `${t('validation.minLength')}: ${min} ${t(
        getSymbolsWordByLength(min),
      )}.`
    }
  }
}

export const maxLength = (max: number) => {
  return (value: string | null = '') => {
    if (value !== null && value.length > 0 && value.length > max) {
      return `${t('validation.maxLength')}: ${max} ${t(
        getSymbolsWordByLength(max),
      )}.`
    }
  }
}

export const regExp = (pattern: PatternType) => {
  return (value: string | null = '') => {
    const regex = new RegExp(pattern.regExp);

    if (value !== null && value.length > 0 && !regex.test(value.replace(/\s/g, ''))) {
      const errorMessage = pattern.message || 'validation.unknownFormat';

      return t(errorMessage);
    }
  }
}

export const simpleValidation =
  ({
    required,
    nullable,
    minLength,
    maxLength,
    pattern,
  }: SimpleValidationProps) =>
  (value: string) => {
    if (required) {
      if (value === null || value.length === 0) {
        return {
          isValid: false,
          message: t('validation.fieldRequiredEmpty'),
        };
      }
    }

    if (nullable && value.length === 0) {
      return {
        isValid: true,
        message: '',
      };
    }

    if (minLength && value.length < minLength) {
      return {
        isValid: false,
        message: `${t('validation.minLength')}: ${minLength} ${t(
          getSymbolsWordByLength(minLength),
        )}.`,
      };
    }

    if (maxLength && value.length > maxLength) {
      return {
        isValid: false,
        message: `${t('validation.maxLength')}: ${maxLength} ${t(
          getSymbolsWordByLength(maxLength),
        )}.`,
      };
    }

    if (pattern) {
      const regex = new RegExp(pattern.regExp);

      if (!regex.test(value)) {
        const errorMessage = pattern.message || 'validation.unknownFormat';

        return {
          isValid: false,
          message: t(errorMessage),
        };
      }
    }

    return {
      isValid: true,
      message: '',
    };
  };

export const fileValidation = ({
  acceptedFormats,
  maxSize
}: FileValidationProps) => (file?: Partial<File>) => {
  if (acceptedFormats
    && file
    && file?.type
    && !acceptedFormats.includes(file?.type)
  ) {
    return t('validation.wrongFileType');
  }

  if (maxSize && file && file?.size && file?.size > maxSize) {
    return t('validation.maxFileSize', { maxFileSize: getFileSizeInMb(maxSize) });
  }

  return '';
}
